import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import PhoneEnabledIcon from '@mui/icons-material/PhoneEnabled';

const Navbar: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 0;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  const scrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleNavLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, _path: string, sectionId?: string) => {
    // Let modifier-clicks (open in new tab/window) and non-primary clicks fall
    // through to the browser's native handling of the real href.
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) {
      return;
    }
    e.preventDefault();

    if (_path && _path !== '/' && location.pathname !== _path) {
      navigate(_path);
      return;
    }

    if (location.pathname === '/') {
      if (sectionId) {
        scrollToSection(sectionId);
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else {
      navigate('/');
      setTimeout(() => {
        if (sectionId) {
          scrollToSection(sectionId);
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 100);
    }
  };

  return (
    <>
      <header className={`sticky top-0 left-0 w-full z-50 transition-all duration-300`}>
        <div className={`w-full flex justify-between items-center px-6 py-4 transition-all duration-300 ${scrolled ? 'bg-white/80 backdrop-blur-md border-b border-gray-100 dark:bg-black/80 dark:border-gray-800' : ''}`}>
          <div className="flex items-center space-x-2">
            {/* Mobile: icon + short name */}
            <img
              src="/images/rknk_logo.webp"
              alt="RK & K Logo"
              className="h-10 w-auto md:hidden"
              onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = "https://placehold.co/40x40/cccccc/333333?text=Logo";
              }}
            />
            <strong className="text-black dark:text-white md:hidden">RK&K</strong>

            {/* Desktop: full wordmark, with a white-text version for dark mode */}
            <img
              src="/images/rknk_secondary_logo.webp"
              alt="RK & K Import-Export, Inc."
              className="hidden md:block md:dark:hidden h-10 w-auto"
            />
            <img
              src="/images/rknk_secondary_logo_dark.webp"
              alt="RK & K Import-Export, Inc."
              className="hidden md:dark:block h-10 w-auto"
            />
          </div>

          <nav className="hidden md:flex space-x-8 font-medium text-sm items-center">
            <a href="/" onClick={(e) => handleNavLinkClick(e, '/')} className="text-black dark:text-white hover:text-gray-500 transition cursor-pointer">
              Home
            </a>
            <a href="/#about" onClick={(e) => handleNavLinkClick(e, '/', 'about')} className="text-black dark:text-white hover:text-gray-500 transition cursor-pointer">
              About Us
            </a>
            <a href="/products" onClick={(e) => handleNavLinkClick(e, '/products')} className="text-black dark:text-white hover:text-gray-500 transition cursor-pointer">
              Products
            </a>
            <a href="/#services" onClick={(e) => handleNavLinkClick(e, '/', 'services')} className="text-black dark:text-white hover:text-gray-500 transition cursor-pointer">
              Services
            </a>
            <a href="/#faq" onClick={(e) => handleNavLinkClick(e, '/', 'faq')} className="text-black dark:text-white hover:text-gray-500 transition cursor-pointer">
              FAQ
            </a>

            {/* DESKTOP CONTACT DISPLAY */}
            <div className="relative group">
              <button className="bg-black text-white px-5 py-2.5 rounded-full text-xs font-bold tracking-widest hover:bg-gray-800 transition dark:bg-white dark:text-black flex items-center gap-2">
                <PhoneEnabledIcon style={{ fontSize: '1rem' }} />
                CALL US
              </button>
              
              {/* Dropdown with phone numbers */}
              <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-[#1E1E1E] border border-gray-100 dark:border-gray-800 rounded-2xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 p-4 z-[60]">
                <div className="space-y-4">
                  <div className="flex flex-col">
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Operations</span>
                    <a href="tel:+639778361411" className="text-sm font-bold text-black dark:text-white hover:underline">+63 977 836 1411</a>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Marketing</span>
                    <a href="tel:+639778031411" className="text-sm font-bold text-black dark:text-white hover:underline">+63 977 803 1411</a>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Sales</span>
                    <a href="tel:+639778021411" className="text-sm font-bold text-black dark:text-white hover:underline">+63 977 802 1411</a>
                  </div>
                </div>
              </div>
            </div>
          </nav>
        </div>
      </header>

      {/* MOBILE BOTTOM NAVBAR */}
      <nav className="fixed bottom-0 left-0 w-full bg-white dark:bg-[#1E1E1E] border-t border-gray-200 dark:border-gray-700 z-50 flex md:hidden items-center justify-around h-16 shadow-lg">
        <a href="/" onClick={(e) => handleNavLinkClick(e, '/')} className="flex flex-1 h-full flex-col items-center justify-center text-xs text-gray-700 dark:text-white hover:text-black transition-colors cursor-pointer">
          <HomeOutlinedIcon style={{ fontSize: '1.5rem' }} />
          <span>Home</span>
        </a>
        <a href="/#about" onClick={(e) => handleNavLinkClick(e, '/', 'about')} className="flex flex-1 h-full flex-col items-center justify-center text-xs text-gray-700 dark:text-white hover:text-black transition-colors cursor-pointer">
          <InfoOutlinedIcon style={{ fontSize: '1.5rem' }} />
          <span>About</span>
        </a>
        <a href="/products" onClick={(e) => handleNavLinkClick(e, '/products')} className="flex flex-1 h-full flex-col items-center justify-center text-xs text-gray-700 dark:text-white hover:text-black transition-colors cursor-pointer">
          <CategoryOutlinedIcon style={{ fontSize: '1.5rem' }} />
          <span>Products</span>
        </a>
        <a href="/#services" onClick={(e) => handleNavLinkClick(e, '/', 'services')} className="flex flex-1 h-full flex-col items-center justify-center text-xs text-gray-700 dark:text-white hover:text-black transition-colors cursor-pointer">
          <BuildOutlinedIcon style={{ fontSize: '1.5rem' }} />
          <span>Services</span>
        </a>
        <a href="/#faq" onClick={(e) => handleNavLinkClick(e, '/', 'faq')} className="flex flex-1 h-full flex-col items-center justify-center text-xs text-gray-700 dark:text-white hover:text-black transition-colors cursor-pointer">
          <HelpOutlineOutlinedIcon style={{ fontSize: '1.5rem' }} />
          <span>FAQ</span>
        </a>
        {/* Mobile Call - Points to Sales by default */}
        <a
          href="tel:+639778021411"
          className="flex flex-1 h-full flex-col items-center justify-center text-xs text-gray-700 dark:text-white hover:text-black transition-colors cursor-pointer"
        >
          <MailOutlineOutlinedIcon style={{ fontSize: '1.5rem' }} />
          <span>Call Us</span>
        </a>
      </nav>
    </>
  );
};

export default Navbar;